import { createEndpoint } from '@remote-ui/rpc';
import { times } from 'lodash-es';

import { acDebug } from '@looko-corp/acloset-react-kit/src/sentry';

import { ThumbnailGeneratorBase } from './ThumbnailGeneratorBase';
import Worker from './ThumbnailGeneratorWorker?worker';
import type { GeneratorParams, GeneratorResult } from './types';

/**
 * Worker 기반의 썸네일 생성기.
 *
 * ### API 제한.
 * - Web Worker
 * - OffscreenCanvas
 * - createImageBitmap
 */
export class ThumbnailGenerator extends ThumbnailGeneratorBase {
  private endpointsPool;
  private endpoints;

  constructor(maxConcurrent: number) {
    super(maxConcurrent);

    acDebug('ThumbnailGenerator is used.');

    // 반 정도만 미리 만들어 둠.
    this.endpointsPool = times(Math.floor(maxConcurrent / 2), this.createEndpoint);
    this.endpoints = [...this.endpointsPool];
  }

  override async dispose() {
    await super.dispose();
    this.endpoints.forEach(v => v.terminate());
    this.endpoints = [];
    this.endpointsPool = [];
    acDebug('ThumbnailGenerator is disposed.');
  }

  enqueue(blob: Blob, data: GeneratorParams) {
    if (this.disposed) {
      return Promise.reject();
    }

    return this.limiter.schedule(
      async (blob, data) => {
        let endpoint = this.endpointsPool.pop();
        if (endpoint == null) {
          endpoint = this.createEndpoint();
          this.endpoints.push(endpoint);
        }

        const objectURL = window.URL.createObjectURL(blob);
        try {
          return await endpoint.call.generateResizeImage({
            ...data,
            objectURL,
          });
        } catch (err) {
          throw err;
        } finally {
          window.URL.revokeObjectURL(objectURL);
          this.endpointsPool.push(endpoint);
        }
      },
      blob,
      data,
    );
  }

  private createEndpoint() {
    return createEndpoint<{
      generateResizeImage(data: GeneratorParams & { objectURL: string }): GeneratorResult;
    }>(new Worker());
  }
}
